'use client'

import { FIXTURES } from './fixtures-flag'
import { normalizeVitalsRoute } from './web-vitals-route'

// GA4 (gtag.js) contract, shared by the public and authenticated surfaces.
//
// WHY THIS IS NOT GOOGLE'S COPY-PASTE SNIPPET
//
// The snippet's `gtag('config', id)` sends a page_view from whatever is in the
// address bar at that moment, and never fires again — which is wrong twice over
// for this app:
//
//  1. It is a single-page app. After the first paint the app router navigates
//     with the history API, so the one page_view the snippet sends is the only
//     one GA would ever see.
//  2. Our authenticated URLs carry TENANT IDENTIFIERS in the query string —
//     `?client_id=…`, `?clientId=…`, `?client=…`, `?view=…`. `page_location` is
//     collected verbatim, so the snippet would ship "which firm looked at which
//     client, and when" to Google as a side effect of counting pageviews.
//
// So page_view is DISABLED on the config and sent by hand, against a path that
// has been through the same allowlist+id-collapse normaliser in web-vitals-route
// (named for the beacon it was written for, which has since been removed).
//
// THE QUERY STRING IS FILTERED, NOT DELETED
//
// An earlier revision dropped the query string wholesale. That kept tenant ids
// out, and also destroyed every campaign: GA4 reads acquisition off
// `page_location`, so `?utm_source=facebook` and `?gclid=…` were being thrown
// away with the ids and 100% of paid traffic would have reported as direct.
// CAMPAIGN_PARAMS below is an ALLOWLIST — a parameter is dropped unless it is
// named here, so an unknown future parameter fails closed rather than leaking.
//
// GA4 PROPERTY SETTINGS THIS ASSUMES (Admin → Data streams → Enhanced
// measurement): "Page changes based on browser history events" must be OFF, or
// GA fires its own page_view on every pushState — double-counting AND reading
// the raw URL, which defeats everything below. See docs/ga4-console-setup.md.

/** GA4 measurement id, inlined at build time. Absent in dev/test/staging. */
export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? ''

/** FIXTURES is QA traffic, not signal — same exclusion the other beacons make. */
export const GA_ENABLED = GA_MEASUREMENT_ID !== '' && !FIXTURES

/** Routes events to GA4 DebugView. Set NEXT_PUBLIC_GA_DEBUG=1 to verify a build. */
const GA_DEBUG = process.env.NEXT_PUBLIC_GA_DEBUG === '1'

export const GA_SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`

/**
 * Which half of the product a pageview came from.
 *
 * Load-bearing: `proxy.ts` rewrites a logged-out `/` to the marketing landing
 * WITHOUT changing the URL, so `usePathname()` returns `/` for both the
 * marketing home and the signed-in app home. Left alone, the top-of-funnel
 * landing page and the product home merge into a single row in every report.
 */
export type GaSurface = 'marketing' | 'auth' | 'app'

/**
 * Every event this app may send. A closed union rather than a free string:
 * GA4 caps a property at 500 distinct event names and silently drops the rest,
 * and a typo mints a new name instead of failing.
 */
export type GaEventName =
  // public / acquisition
  | 'demo_click'
  | 'generate_lead'
  | 'waitlist_submitted'
  | 'sign_up'
  | 'login'
  | 'faq_open'
  | 'scroll_milestone'
  | 'article_download'
  | 'article_section'
  // activation
  | 'source_connect_started'
  | 'source_connect_completed'
  | 'source_connect_failed'
  | 'source_reconnected'
  | 'client_added'
  | 'first_approval'
  | 'inbox_approve'
  | 'inbox_approve_all'
  | 'inbox_recategorize'
  | 'view_saved'
  | 'document_downloaded'

export type GaParams = Record<string, string | number | boolean | undefined>

declare global {
  interface Window {
    dataLayer?: IArguments[]
  }
}

// gtag.js consumes the `arguments` OBJECT that the canonical snippet pushes —
// it reads entries positionally off `length`. A plain array is close enough to
// look right in the console and is not a documented equivalent, so the shim is
// reproduced as Google writes it: no parameters, `arguments` forwarded whole.
// The typed alias below is what callers see; the untyped implementation is what
// makes the forwarding correct.
function gtagPush(): void {
  ;(window.dataLayer ??= []).push(arguments)
}

const gtag: {
  (command: 'js', at: Date): void
  (command: 'config', measurementId: string, params?: Record<string, unknown>): void
  (command: 'event', name: string, params?: Record<string, unknown>): void
  (command: 'set', params: Record<string, unknown>): void
} = gtagPush

/**
 * Query parameters that survive to GA4.
 *
 * Everything here is an ATTRIBUTION parameter — it describes how the visitor
 * arrived, never who they are or what they were looking at. Google's set plus
 * the click ids of the networks we may buy from; harmless to allow before a
 * channel is live, and impossible to backfill after a campaign has run without
 * one.
 */
const CAMPAIGN_PARAMS: ReadonlySet<string> = new Set([
  // GA4 manual campaign parameters
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
  // Google auto-tagging
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  // Meta. NOTE: GA4 does NOT read fbclid as a campaign — it is kept for Meta's
  // own attribution only, which is why every Meta ad must carry UTMs by hand.
  'fbclid',
  // other networks
  'msclkid',
  'ttclid',
  'li_fat_id',
  'twclid',
])

/** A campaign value longer than this is a payload, not a campaign name. */
const MAX_CAMPAIGN_VALUE = 120
/** More distinct campaign keys than this on one URL is not a real campaign. */
const MAX_CAMPAIGN_PARAMS = 12

/**
 * Resolve the route label, accounting for the logged-out root rewrite.
 *
 * See GaSurface. The marketing group renders at `/` for logged-out visitors, so
 * the surface — which is known statically from the route group — is what tells
 * the two homes apart.
 */
export function gaRoutePath(pathname: string, surface: GaSurface): string {
  const route = normalizeVitalsRoute(pathname)
  if (surface === 'marketing' && route === '/') return '/landing'
  return route
}

/** Campaign parameters from a raw query string, filtered and bounded. */
export function gaCampaignQuery(search: string): string {
  if (search === '') return ''
  const kept = new URLSearchParams()
  let n = 0
  for (const [key, value] of new URLSearchParams(search)) {
    if (n >= MAX_CAMPAIGN_PARAMS) break
    if (!CAMPAIGN_PARAMS.has(key.toLowerCase())) continue
    if (value === '') continue
    kept.append(key.toLowerCase(), value.slice(0, MAX_CAMPAIGN_VALUE))
    n += 1
  }
  return kept.toString()
}

/**
 * Absolute URL for GA: a sanitised route plus whatever campaign parameters
 * arrived with it.
 *
 * GA4 derives BOTH its Page path dimensions and its entire acquisition model
 * from `page_location`, so this one string has to satisfy two opposed
 * requirements at once — carry the campaign, carry no tenant.
 */
export function gaPageLocation(origin: string, pathname: string, surface: GaSurface): string {
  const [rawPath = '', rawSearch = ''] = pathname.split('#')[0]!.split('?')
  const route = gaRoutePath(rawPath, surface)
  const query = gaCampaignQuery(rawSearch)
  return `${origin}${route}${query === '' ? '' : `?${query}`}`
}

/**
 * Referrer for GA, sanitised when it is one of our own URLs.
 *
 * An EXTERNAL referrer is the acquisition signal GA exists to report and
 * carries none of our tenant data — passed through untouched. A same-origin
 * referrer is one of our pages, query string and all, so it is normalised the
 * same way as a location.
 *
 * The `${origin}/` prefix test is deliberate: matching bare `origin` alone
 * would also accept `https://legix.ai.example.test/…`.
 */
export function gaPageReferrer(referrer: string, origin: string, surface: GaSurface): string {
  if (referrer === '') return ''
  if (referrer === origin) return gaPageLocation(origin, '/', surface)
  if (!referrer.startsWith(`${origin}/`)) return referrer
  return gaPageLocation(origin, referrer.slice(origin.length), surface)
}

let booted = false

/** Install the tag. Idempotent — a remount must not queue a second `config`. */
export function gaBootstrap(): void {
  if (!GA_ENABLED || booted || typeof window === 'undefined') return
  booted = true
  gtag('js', new Date())
  gtag('config', GA_MEASUREMENT_ID, {
    // We send page_view ourselves, sanitised, on every route.
    send_page_view: false,
    ...(GA_DEBUG ? { debug_mode: true } : {}),
  })
}

/**
 * Send one page_view for a route.
 *
 * `referrer` is passed in rather than read from `document`: on a history-API
 * navigation `document.referrer` still holds the ORIGINAL entry referrer, so
 * the caller supplies the previous in-app location instead.
 */
export function gaPageView(pathname: string, referrer: string, surface: GaSurface): void {
  if (!GA_ENABLED || typeof window === 'undefined') return
  const origin = window.location.origin
  gtag('event', 'page_view', {
    page_location: gaPageLocation(origin, pathname, surface),
    page_referrer: gaPageReferrer(referrer, origin, surface),
    // Titles are static today (root layout + landing metadata). If a page ever
    // renders a client name into its title, it becomes a leak on this line.
    page_title: document.title,
    page_surface: surface,
  })
}

/**
 * Firm name to report, or undefined to fall back to the id alone.
 *
 * GA forbids data identifying a natural person. A firm is a company, so its
 * name is a business identifier — EXCEPT in this market, where a large share of
 * CAS firms are sole proprietorships trading under the owner's own name
 * ("Jane Smith CPA"). For those the org name IS a person's name, so it is
 * withheld and `firm_id` carries the row instead.
 */
export function gaFirmName(orgName: string | null, userName: string | null): string | undefined {
  const org = orgName?.trim()
  if (!org) return undefined
  const user = userName?.trim().toLowerCase()
  if (!user) return org
  // "Jane Smith" vs "Jane Smith CPA" / "Jane Smith Accounting" — containment,
  // not equality, because the suffix is exactly what makes it look like a firm.
  return org.toLowerCase().includes(user) ? undefined : org
}

export interface GaIdentity {
  userId: string | null
  firmId: string | null
  firmName?: string | undefined
  firmPlan?: string | undefined
  userRole?: string | undefined
}

/**
 * Bind the GA4 User-ID and firm to the session so the authenticated side
 * reports people and firms rather than anonymous cookies.
 *
 * Opaque Clerk ids plus the firm's trading name (see gaFirmName). Never an
 * email, a personal name, an org slug, or anything about a client.
 *
 * These land as reportable dimensions ONLY once registered under Admin →
 * Custom definitions; until then they are collected and invisible.
 */
export function gaIdentify(identity: GaIdentity): void {
  if (!GA_ENABLED || typeof window === 'undefined') return
  gtag('set', {
    user_id: identity.userId ?? undefined,
    user_properties: {
      firm_id: identity.firmId ?? undefined,
      firm_name: identity.firmName,
      firm_plan: identity.firmPlan,
      user_role: identity.userRole,
    },
  })
}

/**
 * Send a custom event.
 *
 * Params must be non-PII and low-cardinality: counts, enums, booleans. Never a
 * client id, posting id, party name, amount or search term — those belong to
 * the first-party instruments (decision spine, search analytics, SigNoz), which
 * are tenant-scoped and were built for them.
 */
export function gaEvent(name: GaEventName, params?: GaParams): void {
  if (!GA_ENABLED || typeof window === 'undefined') return
  gtag('event', name, params)
}

/**
 * Claim a once-per-browser latch, returning whether this call won it.
 *
 * Activation milestones ("this firm connected its first ledger", "saved a first
 * view") are only meaningful the first time, and a refresh, a remount or
 * React's development double-effect would otherwise inflate them.
 *
 * Returns false — "not the first" — when storage is unavailable (private mode,
 * blocked site data). That direction is deliberate: under-reporting a milestone
 * is a smaller lie than reporting every visit in a private window as a brand
 * new activation.
 */
export function gaFirstTime(key: string): boolean {
  // Short-circuit when the tag is off (dev, tests, staging): nothing would be
  // sent anyway, and this keeps every non-GA environment from touching storage
  // at all — which matters because Node 26's own `localStorage` global shadows
  // jsdom's and is inert without --localstorage-file.
  if (!GA_ENABLED || typeof window === 'undefined') return false
  const storageKey = `ga_once:${key}`
  try {
    if (window.localStorage.getItem(storageKey) !== null) return false
    window.localStorage.setItem(storageKey, '1')
    return true
  } catch {
    return false
  }
}

/**
 * Fire an event at most once per browser.
 *
 * For milestones that have their OWN event name (`first_approval`). Where the
 * milestone is instead a flag on a recurring event, use `gaFirstTime` and pass
 * `is_first` — emitting a second event for the same action doubles its count.
 */
export function gaEventOnce(key: string, name: GaEventName, params?: GaParams): void {
  if (!GA_ENABLED || typeof window === 'undefined') return
  if (!gaFirstTime(key)) return
  gaEvent(name, params)
}
